'use client';

import React, { useMemo, useState } from 'react';
import type { Reminder } from '@/core/domain';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { Button } from '@/core/presentation/components/ui/Button';
import {
  LEMBRETES_CAL_ARIA_AGENDADOS,
  LEMBRETES_CAL_ARIA_A_RECEBER,
  LEMBRETES_CAL_ARIA_CONTATADOS,
  LEMBRETES_CAL_ARIA_RECEBIDOS_DIA,
  LEMBRETES_CAL_ARIA_SEM_DADOS,
  LEMBRETES_CAL_CLEAR_DAY,
  LEMBRETES_CAL_LEGEND_AGENDADOS_DESC,
  LEMBRETES_CAL_LEGEND_AGENDADOS_TITLE,
  LEMBRETES_CAL_LEGEND_A_RECEBER_DESC,
  LEMBRETES_CAL_LEGEND_A_RECEBER_TITLE,
  LEMBRETES_CAL_LEGEND_BOX_TITLE,
  LEMBRETES_CAL_LEGEND_CONTATADOS_DESC,
  LEMBRETES_CAL_LEGEND_CONTATADOS_TITLE,
  LEMBRETES_CAL_LEGEND_RECEBIDO_PASSADO_DESC,
  LEMBRETES_CAL_LEGEND_RECEBIDO_PASSADO_TITLE,
  LEMBRETES_CAL_NEXT_MONTH,
  LEMBRETES_CAL_PREV_MONTH,
  LEMBRETES_CAL_TITLE,
} from '@/features/app/presentation/constants/lembretes-calendario-copy';
import {
  countFutureRemindersOnDay,
  funnelMetricsForDay,
} from '@/features/app/presentation/utils/reminder-day-metrics';
import {
  calendarDayButtonClassName,
  calendarDayNumberClassName,
  calendarDaySecondaryTextClassName,
  isCalendarToday,
} from '@/features/app/presentation/utils/calendar-day-cell';

const REMINDERS_CAL_CELL_H = 'min-h-[4.5rem] sm:min-h-[5.5rem]';

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function dateKeyFromParts(year: number, monthIndex: number, day: number): string {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

function ariaLabelDayButton(
  day: number,
  total: number,
  contacted: number,
  scheduled: number,
  futureOnDay: number
): string {
  const dayLabel = `Dia ${pad2(day)}`;
  const parts: string[] = [];
  if (futureOnDay > 0) {
    parts.push(`${futureOnDay} ${LEMBRETES_CAL_ARIA_A_RECEBER}`);
  }
  if (total > 0) {
    parts.push(
      `${total} ${LEMBRETES_CAL_ARIA_RECEBIDOS_DIA}, ${contacted} ${LEMBRETES_CAL_ARIA_CONTATADOS}, ${scheduled} ${LEMBRETES_CAL_ARIA_AGENDADOS}`
    );
  }
  if (parts.length === 0) {
    return `${dayLabel}: ${LEMBRETES_CAL_ARIA_SEM_DADOS}`;
  }
  return `${dayLabel}: ${parts.join('; ')}`;
}

function buildMonthCells(year: number, monthIndex: number): (number | null)[] {
  const first = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const startOffset = first.getDay();
  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i += 1) {
    cells.push(null);
  }
  for (let d = 1; d <= lastDay; d += 1) {
    cells.push(d);
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
}

export interface RemindersCalendarProps {
  reminders: Reminder[];
  /** Itens a serem enviados (sem envio) — destacam dias com manutenção prevista e permitem seleção. */
  futureReminders?: Reminder[];
  selectedDayKey: string | null;
  onSelectDay: (dayKey: string | null) => void;
  /** Conteúdo exibido abaixo da legenda (ex.: lista do dia). */
  belowLegend?: React.ReactNode;
}

export const RemindersCalendar: React.FC<RemindersCalendarProps> = ({
  reminders,
  futureReminders = [],
  selectedDayKey,
  onSelectDay,
  belowLegend,
}) => {
  const [view, setView] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const year = view.getFullYear();
  const monthIndex = view.getMonth();

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(
        new Date(year, monthIndex, 1)
      ),
    [year, monthIndex]
  );

  const cells = useMemo(() => buildMonthCells(year, monthIndex), [year, monthIndex]);

  const goPrev = () => {
    setView(new Date(year, monthIndex - 1, 1));
  };

  const goNext = () => {
    setView(new Date(year, monthIndex + 1, 1));
  };

  return (
    <div className="rounded-xl border border-border-light bg-white p-3 sm:p-4 min-w-0">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-navy-dark flex items-center gap-2">
            <AppIcon name="CalendarDaysIcon" size={18} className="text-cyan shrink-0" />
            {LEMBRETES_CAL_TITLE}
          </h3>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="!px-2"
              aria-label={LEMBRETES_CAL_PREV_MONTH}
              onClick={goPrev}
            >
              <AppIcon name="ChevronLeftIcon" size={18} />
            </Button>
            <span className="text-sm font-medium text-navy-dark capitalize min-w-[10rem] text-center px-1">
              {monthLabel}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="!px-2"
              aria-label={LEMBRETES_CAL_NEXT_MONTH}
              onClick={goNext}
            >
              <AppIcon name="ChevronRightIcon" size={18} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] sm:text-xs font-medium text-text-muted uppercase tracking-wide">
          {WEEK_DAYS.map((d) => (
            <div key={d} className="py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day == null) {
            return <div key={`empty-${idx}`} className={REMINDERS_CAL_CELL_H} />;
          }
          const dayKey = dateKeyFromParts(year, monthIndex, day);
          const { total, contacted, scheduled } = funnelMetricsForDay(reminders, dayKey);
          const futureOnDay = countFutureRemindersOnDay(futureReminders, dayKey);
          const selected = selectedDayKey === dayKey;
          const hasData = total > 0 || futureOnDay > 0;
          const todayCell = isCalendarToday(dayKey);
          const secondaryText = calendarDaySecondaryTextClassName(dayKey);
          const metricStrong = todayCell ? 'text-white' : 'text-navy-dark';

          return (
            <button
              key={dayKey}
              type="button"
              onClick={() => onSelectDay(selected ? null : dayKey)}
              className={calendarDayButtonClassName({
                dayKey,
                selected,
                hasData,
                cellMinHeightClass: REMINDERS_CAL_CELL_H,
              })}
              aria-pressed={selected}
              aria-label={ariaLabelDayButton(day, total, contacted, scheduled, futureOnDay)}
            >
              <span className={`block text-sm font-semibold ${calendarDayNumberClassName(dayKey, selected)}`}>
                {day}
              </span>
              {hasData ? (
                <div className="mt-1.5 space-y-1" aria-hidden="true">
                  {futureOnDay > 0 ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full bg-violet-600"
                        title={LEMBRETES_CAL_LEGEND_A_RECEBER_TITLE}
                      />
                      <span className={`text-[10px] font-semibold tabular-nums ${metricStrong} leading-none`}>
                        {futureOnDay}
                      </span>
                    </div>
                  ) : null}
                  {total > 0 ? (
                    <>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full bg-blue-500"
                          title={LEMBRETES_CAL_LEGEND_RECEBIDO_PASSADO_TITLE}
                        />
                        <span className={`text-[10px] font-semibold tabular-nums ${metricStrong} leading-none`}>
                          {total}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
                          title={LEMBRETES_CAL_LEGEND_CONTATADOS_TITLE}
                        />
                        <span className={`text-[10px] tabular-nums ${secondaryText} leading-none`}>
                          {contacted}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full bg-emerald-500"
                          title={LEMBRETES_CAL_LEGEND_AGENDADOS_TITLE}
                        />
                        <span className={`text-[10px] tabular-nums ${secondaryText} leading-none`}>
                          {scheduled}
                        </span>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <span
                  className={`mt-1.5 block text-[10px] leading-none ${
                    todayCell ? 'text-white/55' : 'text-text-muted/70'
                  }`}
                >
                  —
                </span>
              )}
            </button>
          );
        })}
        </div>
      </div>

      {selectedDayKey ? (
        <div className="mt-1 flex justify-end">
          <Button type="button" variant="ghost" size="sm" className="text-cyan" onClick={() => onSelectDay(null)}>
            {LEMBRETES_CAL_CLEAR_DAY}
          </Button>
        </div>
      ) : null}

      <div
        className="rounded-lg border border-border-light bg-[#fafbfc] px-3 py-2.5 space-y-2 text-[11px] sm:text-xs text-text-muted"
        role="region"
        aria-label="Legenda do calendário"
      >
        <p className="font-semibold text-navy-dark text-xs">{LEMBRETES_CAL_LEGEND_BOX_TITLE}</p>
        <ul className="space-y-1.5 list-none pl-0 m-0">
          <li className="flex gap-2">
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-violet-600" aria-hidden />
            <span>
              <strong className="text-navy-dark">{LEMBRETES_CAL_LEGEND_A_RECEBER_TITLE}</strong> —{' '}
              {LEMBRETES_CAL_LEGEND_A_RECEBER_DESC}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500" aria-hidden />
            <span>
              <strong className="text-navy-dark">{LEMBRETES_CAL_LEGEND_RECEBIDO_PASSADO_TITLE}</strong> —{' '}
              {LEMBRETES_CAL_LEGEND_RECEBIDO_PASSADO_DESC}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
            <span>
              <strong className="text-navy-dark">{LEMBRETES_CAL_LEGEND_CONTATADOS_TITLE}</strong> —{' '}
              {LEMBRETES_CAL_LEGEND_CONTATADOS_DESC}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
            <span>
              <strong className="text-navy-dark">{LEMBRETES_CAL_LEGEND_AGENDADOS_TITLE}</strong> —{' '}
              {LEMBRETES_CAL_LEGEND_AGENDADOS_DESC}
            </span>
          </li>
        </ul>
      </div>

      {belowLegend != null ? (
        <div className="mt-3 pt-1 border-t border-border-light min-w-0">{belowLegend}</div>
      ) : null}
    </div>
  );
};
